import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Activity, LogOut, Shield, Sparkles, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "@/components/ui/sheet";
import { useState } from "react";

function XIcon({ className, size = 16 }: { className?: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

const links = [
  { to: "/dashboard", label: "Dashboard" },
  { to: "/top-5", label: "Top 5" },
  { to: "/picks", label: "All Picks" },
  { to: "/history", label: "History" },
  { to: "/pricing", label: "Pricing" },
  { to: "/account", label: "Account" },
];

export default function Navbar() {
  const { user, isAdmin, signOut } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-border/60 backdrop-blur-xl bg-background/70">
      <div className="container flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-display text-xl font-bold">
          <span className="grid place-items-center w-9 h-9 rounded-xl bg-gradient-primary text-primary-foreground shadow-glow">
            <Activity className="w-5 h-5" />
          </span>
          <span>Alea<span className="te-grad-text">Edge</span></span>
        </Link>
        {user && (
          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-medium transition ${
                    isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {isAdmin && (
              <>
                <NavLink
                  to="/admin"
                  end
                  className={({ isActive }) =>
                    `ml-1 px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 ${
                      isActive ? "bg-accent text-accent-foreground" : "text-accent hover:bg-accent/10"
                    }`
                  }
                >
                  <Shield className="w-4 h-4" /> Admin
                </NavLink>
                <NavLink
                  to="/admin/articles"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 ${
                      isActive ? "bg-accent text-accent-foreground" : "text-accent hover:bg-accent/10"
                    }`
                  }
                >
                  <Shield className="w-4 h-4" /> Articles
                </NavLink>
                <NavLink
                  to="/admin/users"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 ${
                      isActive ? "bg-accent text-accent-foreground" : "text-accent hover:bg-accent/10"
                    }`
                  }
                >
                  <Shield className="w-4 h-4" /> Users
                </NavLink>
              </>
            )}
          </nav>
        )}
        <div className="flex items-center gap-2">
          {/* X social — desktop + mobile */}
          <a
            href="https://x.com/aleaedge"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="AleaEdge on X"
            className="group inline-grid place-items-center w-8 h-8 rounded-lg text-muted-foreground hover:text-primary hover:-translate-y-0.5 hover:shadow-[0_0_14px_hsl(152_83%_48%_/0.28)] transition-all duration-300"
          >
            <XIcon size={16} className="transition-opacity duration-300 group-hover:opacity-100 opacity-60" />
          </a>

          {user ? (
            <>
              <Button variant="ghost" size="sm" onClick={async () => { await signOut(); nav("/"); }}>
                <LogOut className="w-4 h-4" />
              </Button>
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="sm" className="md:hidden" aria-label="Open menu">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-72">
                  <SheetTitle className="mb-4">Menu</SheetTitle>
                  <nav className="flex flex-col gap-1">
                    {links.map((l) => (
                      <NavLink
                        key={l.to}
                        to={l.to}
                        onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                          `px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                            isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                          }`
                        }
                      >
                        {l.label}
                      </NavLink>
                    ))}
                    {isAdmin && (
                      <>
                        <NavLink
                          to="/admin"
                          end
                          onClick={() => setOpen(false)}
                          className={({ isActive }) =>
                            `mt-1 px-3 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-1.5 ${
                              isActive ? "bg-accent text-accent-foreground" : "text-accent hover:bg-accent/10"
                            }`
                          }
                        >
                          <Shield className="w-4 h-4" /> Admin
                        </NavLink>
                        <NavLink
                          to="/admin/articles"
                          onClick={() => setOpen(false)}
                          className={({ isActive }) =>
                            `px-3 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-1.5 ${
                              isActive ? "bg-accent text-accent-foreground" : "text-accent hover:bg-accent/10"
                            }`
                          }
                        >
                          <Shield className="w-4 h-4" /> Articles
                        </NavLink>
                        <NavLink
                          to="/admin/users"
                          onClick={() => setOpen(false)}
                          className={({ isActive }) =>
                            `px-3 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-1.5 ${
                              isActive ? "bg-accent text-accent-foreground" : "text-accent hover:bg-accent/10"
                            }`
                          }
                        >
                          <Shield className="w-4 h-4" /> Users
                        </NavLink>
                      </>
                    )}
                    <div className="mt-3 pt-3 border-t border-border/40 flex items-center gap-3 px-3">
                      <a
                        href="https://x.com/aleaedge"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="AleaEdge on X"
                        className="group inline-grid place-items-center w-8 h-8 rounded-lg text-muted-foreground hover:text-primary hover:-translate-y-0.5 hover:shadow-[0_0_14px_hsl(152_83%_48%_/0.28)] transition-all duration-300"
                        onClick={() => setOpen(false)}
                      >
                        <XIcon size={16} className="transition-opacity duration-300 group-hover:opacity-100 opacity-60" />
                      </a>
                    </div>
                  </nav>
                </SheetContent>
              </Sheet>
            </>
          ) : (
            <>
              <Link to="/pricing"><Button variant="ghost" size="sm">Pricing</Button></Link>
              <Link to="/auth"><Button size="sm" className="bg-gradient-primary text-primary-foreground hover:opacity-90"><Sparkles className="w-4 h-4 mr-1" />Sign in</Button></Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
